import type { NavigateFunction } from 'react-router-dom';
import type { CombinedUploadSessionResult } from '../../types/upload';
import { coachStorageKey, COACH_STORAGE_SUFFIX } from '../coachStorage';

export interface UploadBatchSession {
  sessionId: string;
  sheetName: string;
  sessionName?: string | null;
}

export interface UploadBatchContext {
  sessions: UploadBatchSession[];
  teamId?: string;
  teamName?: string;
  returnTo?: string;
  createdAt: number;
}

export interface UploadBatchLocationState {
  uploadBatch?: UploadBatchContext;
}

const BATCH_TTL_MS = 24 * 60 * 60 * 1000;

function uploadBatchStorageKey(userId: string): string {
  return coachStorageKey(userId, COACH_STORAGE_SUFFIX.uploadBatch);
}

export function buildUploadBatchContext(
  results: CombinedUploadSessionResult[],
  options?: { teamId?: string; teamName?: string }
): UploadBatchContext {
  const teamId = options?.teamId;
  return {
    sessions: results.map((result) => ({
      sessionId: result.sessionId,
      sheetName: result.sheetName,
      sessionName: result.sessionName,
    })),
    teamId,
    teamName: options?.teamName,
    returnTo: teamId ? `/coach/teams/${teamId}?tab=sessions` : undefined,
    createdAt: Date.now(),
  };
}

export function persistUploadBatch(batch: UploadBatchContext, userId: string): void {
  try {
    sessionStorage.setItem(uploadBatchStorageKey(userId), JSON.stringify(batch));
  } catch {
    // Ignore quota / private mode errors
  }
}

export function readUploadBatch(userId: string): UploadBatchContext | null {
  const storageKey = uploadBatchStorageKey(userId);
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UploadBatchContext;
    if (!parsed?.sessions?.length || !parsed.createdAt) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    if (Date.now() - parsed.createdAt > BATCH_TTL_MS) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(storageKey);
    return null;
  }
}

export function clearUploadBatch(userId: string): void {
  sessionStorage.removeItem(uploadBatchStorageKey(userId));
}

export function isSessionInUploadBatch(
  batch: UploadBatchContext | null | undefined,
  sessionId: string | null | undefined
): boolean {
  if (!batch || !sessionId) return false;
  return batch.sessions.some((session) => session.sessionId === sessionId);
}

export function getActiveUploadBatch(
  batch: UploadBatchContext | null | undefined,
  sessionId: string | null | undefined
): UploadBatchContext | null {
  return isSessionInUploadBatch(batch, sessionId) ? (batch ?? null) : null;
}

export function mergeUploadBatchFromLocationState(
  state: UploadBatchLocationState | null | undefined,
  userId: string
): UploadBatchContext | null {
  if (state?.uploadBatch?.sessions?.length) {
    persistUploadBatch(state.uploadBatch, userId);
    return state.uploadBatch;
  }
  return readUploadBatch(userId);
}

export function navigateToBatchSession(
  navigate: NavigateFunction,
  sessionId: string,
  batch: UploadBatchContext,
  userId: string
): void {
  persistUploadBatch(batch, userId);
  navigate(`/coach/dashboard/${sessionId}`, { state: { uploadBatch: batch } });
}

export function labelForBatchSession(session: UploadBatchSession): string {
  if (session.sessionName) {
    return `${session.sheetName} — ${session.sessionName}`;
  }
  return session.sheetName;
}
